// Domain / API-contract types for bbbk. Two kinds live here:
//
//   1. Insert shapes inferred from the Drizzle tables (`New<Name>`) — the
//      write payloads.
//   2. The API wire/domain types the app passes around (Booking, Owner, …).
//      These are the shapes GET /api/* returns — a booking with its dogs and
//      owner contacts embedded, an owner with its derived aggregates — NOT
//      the flat DB rows. apps/web and apps/api both import them from here; no
//      parallel definitions (see docs/remediation/audit-remediation-epic.md).
//
// Enums (KennelArea, BookingStatus, PaymentStatus, …) live in ./enums and are
// re-exported from the package barrel.

import type {
  activityEvents,
  bookingDogs,
  bookings,
  capacityBlocks,
  handoverDays,
  deskNotifications,
  dogs,
  dogsByDate,
  holidays,
  idempotencyKeys,
  ownerContacts,
  owners,
  payMarks,
  portalInvites,
  proofs,
  shifts,
  staff,
  viewings,
} from "./schema";
import type { AvailabilityDay, DayLevel, RangeVerdict, StayQuote } from "./stay-request";
import type { AllowlistRole, BookingSource, BookingStatus, CapacityLine, DogArea, DogSize, DogSizeStored, HandoverDayRule, KennelArea, LeaveKind, PaymentKind, PaymentSource, PaymentStatus, Period, PortalSettingKey, ViewingSlot, ViewingStatus } from "./enums";

// --- insert shapes (write payloads) -----------------------------------

import type { PaymentProof } from "./proofs";
import type { CardProof } from "./cards";

export type NewOwner = typeof owners.$inferInsert;
export type NewOwnerContact = typeof ownerContacts.$inferInsert;
export type NewDog = typeof dogs.$inferInsert;
export type NewBooking = typeof bookings.$inferInsert;
export type NewBookingDog = typeof bookingDogs.$inferInsert;
export type NewStaff = typeof staff.$inferInsert;
export type NewShift = typeof shifts.$inferInsert;
export type NewPayMark = typeof payMarks.$inferInsert;
export type NewHoliday = typeof holidays.$inferInsert;
export type NewDogsByDate = typeof dogsByDate.$inferInsert;
export type NewIdempotencyKey = typeof idempotencyKeys.$inferInsert;
export type NewActivityEvent = typeof activityEvents.$inferInsert;
export type NewProof = typeof proofs.$inferInsert;
export type NewPortalInvite = typeof portalInvites.$inferInsert;
export type NewDeskNotification = typeof deskNotifications.$inferInsert;
export type NewCapacityBlock = typeof capacityBlocks.$inferInsert;
export type NewHandoverDay = typeof handoverDays.$inferInsert;

// --- DB row types with no distinct app-side view model ----------------
// (Staff/Shift/PayMark/Holiday moved to the wire-type section below — the API
// emits a distinct camelCase view of each, so the raw $inferSelect rows no
// longer earn an export nobody imports.)

export type Dog = typeof dogs.$inferSelect;
export type DogsByDate = typeof dogsByDate.$inferSelect;
export type ActivityEvent = typeof activityEvents.$inferSelect;
export type IdempotencyKey = typeof idempotencyKeys.$inferSelect;

// --- API wire / domain types ------------------------------------------

// A dog as embedded in a booking row.
export interface BookingDog {
  id: string;
  name: string;
  // The STORED size vocabulary, not KennelArea: legacy dogs carry
  // 'mix'/'house'/'unknown' (dogs_size_check), and the embed passes the column
  // through verbatim — the previous `KennelArea` claim was narrower than what
  // the API actually emits.
  size?: DogSizeStored | null;
  breed?: string | null;
  vaccinesDue?: string | null;
  /** The kennel-cough date — the desk's card judges the earlier of the two (PR #260 review). */
  kcDue?: string | null;
  meds?: string | null; // medication notes — drives the per-dog meds flag (booking-flags)
  notes?: string | null; // free-text dog note — drives the per-dog note flag (booking-flags)
  rateOverride?: string | null; // per-dog nightly rate override (booking_dogs.rate_override)
  // WHERE THIS DOG IS on THIS booking (dogs-in-areas AC1) — `booking_dogs.area`,
  // one of DOG_AREAS. NULL on a historical booking nobody has assigned; the
  // modal draws that row as unplaced and refuses to save over it (AC11).
  //
  // NOT the dog's size. That distinction is the whole story: `size` says what
  // the dog is, `area` says where it sleeps, and capacity now counts the
  // second (AC6).
  area?: DogArea | null;
  /**
   * WHICH OF THIS STAY'S CARE FLAGS THIS DOG IS ON (per-dog-care-flags, 0034).
   *
   * NULL IS NOT FALSE. Every booking taken before 0034 has nulls across the
   * join, and that means *nobody has ever been asked* — not *not this dog*. The
   * desk's first tick seeds from the household record; nothing backfills.
   *
   * `needsMeds` rather than `meds` because `meds` above is the dog's own
   * instructions TEXT. Two fields, one name, and no way to tell which won.
   */
  foodAtHouse?: boolean | null;
  needsMeds?: boolean | null;
  keepApart?: boolean | null;
  /**
   * What this dog's medication was AT CHECK-IN, stamped once on arrival.
   *
   * Null while the stay is still ahead of us, and the app reads `meds` above
   * live — which is how a household edit carries forward to every future
   * booking with nothing copied. See per-dog-care-flags-two-clocks.
   */
  medsAtArrival?: string | null;
  /** The contact whose words `medsAtArrival` are — a customer's request (0036). */
  medsBy?: number | null;
  /** The contact who asked for this dog to be kept apart — a customer's request (0037). */
  apartBy?: number | null;
}

// An owner contact as embedded in a booking row.
export interface OwnerContact {
  // The row's own id. Optional because a contact being ADDED has none yet, and
  // because the two /context bundles predate it — but every read the desk uses
  // now supplies it, and an edit MUST send it back: `PATCH /api/owners/:id`
  // reconciles by id, and a contact that arrives without one is treated as new.
  // `portal_invites.contact_id` and a customer's allowlist entry both name this
  // id, so losing it orphans a registration link and a binding with it.
  id?: number;
  sortOrder: number;
  /** The full name, composed from the two below — and the source of truth. */
  name: string;
  /** R-DESK-18 — the desk edits the name as two fields. */
  firstName?: string | null;
  /** OPTIONAL: plenty of contacts are a first name and a phone number. */
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  /**
   * ISO timestamp once this contact has registered through the portal, null
   * otherwise. The desk READS it and never writes it.
   *
   * It is here because a registered contact's `email` is their SIGN-IN
   * IDENTITY — the allowlist is keyed on it — so the desk must not type over
   * it. Optional because the two `/context` bundles predate the field.
   */
  registeredAt?: string | null;
  /**
   * `{"firstName": <contactId>, "lastName": <contactId>}`. NOT `email` — a
   * registered contact's email is locked outright and `registeredAt` already
   * says it is theirs, so a second claim on the same fact would be noise.
   */
  confirmedBy?: ConfirmedBy | null;
}

// One booking row from GET /api/bookings (R1 + R2 + R3 in one envelope).
export interface Booking {
  id: string;
  ownerId: string;
  checkIn: string; // ISO YYYY-MM-DD
  checkOut: string; // ISO YYYY-MM-DD
  area: KennelArea;
  status: BookingStatus;
  paymentStatus?: PaymentStatus;
  total?: string | null; // Postgres numeric → exact string, never float
  deposit?: string | null; // amount paid; the desk "due" = total − deposit
  // The booking's agreed nightly rate from the source sheet (all dogs, per
  // night): total = ratePerNight × nights. The drawer reflects THIS, not the
  // area size rate; absent ⇒ a brand-new booking with no agreed rate yet.
  ratePerNight?: string | null;
  createdAt?: string; // ISO timestamp; the "date added" sort source (OQ-2)
  updatedAt?: string; // ISO timestamp; the "last updated" sort source (trigger-maintained)
  // Care flags (staff-food-at-house): booking-level, admin-set. Each drives
  // its flag chip + staff Today briefing card. Keep-apart rides mixOk below.
  foodAtHouse?: boolean;
  meds?: boolean;
  mixOk?: boolean;
  // Owner-level "do not mix" flag, carried onto each of the owner's bookings so
  // the row indicator can show it (do-not-mix lives on the owner, not the stay).
  doNotMix?: boolean;
  // Owner's lifetime booking count, carried onto each row so the desk/table can
  // show the first-booking flag (=== 1) without a second fetch (booking-flags).
  ownerVisits?: number | null;
  // Booking-level note, surfaced on the list so the note flag's hover can reveal
  // it in place — not just on the detail drawer (booking-flags AC5).
  notes?: string | null;
  /** The contact who wrote the note — a customer's request (0037); null once the desk changes it. */
  notesBy?: number | null;
  // Inherited note (booking-flags): the owner's most recent PRIOR booking note,
  // carried onto this booking for display when it has none of its own. Derived
  // server-side; drives the note flag + the "from previous booking" highlight.
  carriedNote?: { text: string; fromCheckIn: string } | null;
  dogs: BookingDog[];
  ownerContacts: OwnerContact[];
  /** A payment screenshot is waiting on Patricia — the Daily's row says *Screenshot in* (deposit-proof AC8). */
  proofPending?: boolean;
  /** Where the booking was made — the desk or the customer's app; on the list row since 2026-10-11. */
  source?: BookingSource;
}

// Full booking detail from GET /api/bookings/:id — the list shape plus the
// drawer-only columns. Drives the BookingDrawer (view-booking-details et al).
export interface BookingDetail extends Booking {
  dropOff: string; // 'am' | 'pm' (legacy rows may hold a time)
  collection: string;
  deposit: string | null; // money received to date — the mirror of `payments`, see schema.ts
  notes: string | null;
  // The ledger behind `deposit`, newest first. The modal renders and edits this;
  // the sum is the only thing `deposit` is allowed to be.
  payments: Payment[];
  /** Where the booking was made — the desk or the customer's app (0022). */
  source?: BookingSource;
  /** The deposit the customer is asked for: min(deposit_minimum, total) — on every booking, whoever made it (2026-10-12). */
  depositAsked?: number | null;
  /** Every payment screenshot on the booking, newest first — the Money card's state (deposit-proof AC9a). */
  proofs?: PaymentProof[];
  /** Every vaccination card of the booking's dogs, newest first — the Vaccinations card's state (vaccination-cards AC13). */
  cards?: CardProof[];
  /** The household has a registered app login, so the customer can send a screenshot for it (2026-10-12). */
  inApp?: boolean;
}

/**
 * field name -> the `owner_contacts.id` that last wrote it (migration 0033).
 *
 * PER FIELD, because a contact can confirm a dog's breed and not its name
 * (`desk-sees-customer-answers` AC1). A key present means the household told us
 * that value; a key absent means the desk's own typing, and the two are what
 * the household modal draws a mark from. A desk edit DELETES its key.
 */
export type ConfirmedBy = Record<string, number>;

// A dog as returned by GET /api/owners (owner's dogs, for the drawer picker).
export interface OwnerDog {
  id: string;
  ownerId: string;
  name: string;
  breed?: string | null;
  size?: string | null;
  vaccinesDue?: string | null;
  /** The kennel-cough date — the desk's card judges the earlier of the two (PR #260 review). */
  kcDue?: string | null;
  /**
   * Medication instructions, or null for none.
   *
   * The desk modal had no medication field at all — the column was written only
   * by the customer's registration and read only by the booking flags. Freddi
   * put a Meds tile beside Keep apart on 2026-09-16, so the desk can now see
   * and change what the household told us.
   */
  meds?: string | null;
  /** The customer's own answer, per dog. NULL MEANS NOBODY HAS ANSWERED — not *mixes well*. */
  keepApart?: boolean | null;
  /** The newest vaccination card the customer uploaded, for the desk to open (deposit-proof AC15); null when none. */
  cardProofId?: string | null;
  /** That card's MIME type — a PDF opens in a PDF viewer, not an <img> (PR #255 review). */
  cardProofMime?: string | null;
  /**
   * Which of `name`, `breed`, `meds`, `keepApart` and `vaccinesDue` the
   * household gave us, and who. The date is in the same map as the rest
   * because registration writes the card's claimed date ONTO the dog
   * (2026-09-16) — there is no side channel for a claim beside the column.
   */
  confirmedBy?: ConfirmedBy | null;
}

// An owner household from GET /api/owners — contacts (primary-first) + dogs.
export interface Owner {
  id: string;
  address?: string | null;
  doNotMix?: boolean; // household "do not mix" flag — drives the red row indicator
  /** `{"address": <contactId>}` — the household address is customer-written too. */
  confirmedBy?: ConfirmedBy | null;
  createdAt?: string | null; // when the household was added (page list; null in picker mode)
  updatedAt?: string | null; // household last-updated (page list; null in picker mode); trigger-maintained
  visits?: number; // derived: lifetime booking count (GET /api/owners aggregate)
  totalSpend?: number; // derived: sum of booking totals
  boardingNow?: boolean; // derived: has a dog in residence today
  contacts: OwnerContact[];
  dogs: OwnerDog[];
}

// GET /api/owners/:id — the single-owner detail: the list shape plus the
// archived flag (the drawer reads it to decide Archive vs Restore). Also the
// `owner` member of the /context bundles.
export interface OwnerDetail extends Owner {
  archived: boolean;
}

// One entry of an owner's booking timeline (GET /api/owners/:id/bookings and
// the /context bundles), newest stay first. A lean projection of the booking —
// dog NAMES pre-joined into one display string, no embeds.
export interface OwnerTimelineEntry {
  id: string;
  checkIn: string; // ISO YYYY-MM-DD
  checkOut: string; // ISO YYYY-MM-DD
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  total: string | null; // Postgres numeric → exact string
  notes: string | null;
  createdAt: string; // ISO timestamp
  dogs: string; // comma-joined dog names, '' when the stay has none
  /** The stay's dog ids — the customer app matches on these; the desk does not. */
  dogIds?: string[];
  /** The names, one per id in the same order, read off the link so a retired dog keeps its name. */
  dogNames?: string[];
  // The halves of the day (customer-sign-in-registration AC35): the receipt's
  // compact stay card says "Sat 17 Oct · morning". Optional because the two
  // /context bundles and older callers never asked for them; the timeline read
  // now selects both, and the desk simply does not draw them.
  dropOff?: string;
  collection?: string;
  /** The booking's reference; the customer app's openers carry it (customer-home-stays). */
  reference?: string | null;
}

/**
 * A STAY AS THE CUSTOMER'S OWN SCREENS NEED IT, and no more.
 *
 * `/portal/me` returned the desk's whole `OwnerTimelineEntry` — `notes`,
 * `total`, `paymentStatus`, `createdAt` — none of which any customer screen
 * draws, all of which any customer could read straight out of the response
 * (P1.11, 2026-09-15). `notes` is the sharp one: it is the desk's own writing
 * about the household.
 *
 * Six fields, each one drawn somewhere: the stay rows on Home read `id`,
 * `checkIn`, `checkOut`, `status` and `dogs`; the receipt's compact card reads
 * the two halves of the day (AC35).
 */
export interface PortalStay {
  id: string;
  checkIn: string; // ISO YYYY-MM-DD
  checkOut: string; // ISO YYYY-MM-DD
  status: BookingStatus;
  dogs: string; // comma-joined dog names, '' when the stay has none
  /** The stay's dog ids. Matching on NAMES put two dogs called Max on one stay. */
  dogIds: string[];
  dropOff?: string;
  collection?: string;
  /**
   * THE MONEY, AS THREE NUMBERS AND A WORD (customer-home-stays AC11). The
   * stay card reads *Stay · Deposit · Due*; nothing else of the ledger leaves
   * the server. `deposit` is what has been received — or, on a `requested`
   * stay where nothing has, the deposit the wizard quoted
   * (`min(deposit_minimum, total)`, computed on read: nothing is promised
   * until Patricia accepts, so there is no quote to persist). `due` is what
   * is still owed; it, never `paymentStatus`, says whether a stay is owed.
   */
  total: number;
  deposit: number;
  due: number;
  paymentStatus: PaymentStatus;
  /** The booking's reference — every WhatsApp opener about a stay carries it. */
  reference: string | null;
  /** A payment screenshot waiting on Patricia, by kind — the sheet is locked while one is (deposit-proof AC18). */
  proofPending?: PaymentKind | null;
  /** The newest screenshot of a line still due was refused — Home says it could not be matched (AC11). */
  proofRefused?: PaymentKind | null;
}

// One person on a household, as the desk's App access column draws them
// (household-app-access, R-DESK-16). The API derives `state` from the invite
// row plus the allowlist; the desk never computes it from the timestamps below
// and the timestamps are here only so the row can say WHEN.
//
// SHARED BECAUSE BOTH ENDS READ IT. `apps/api` builds this shape and
// `apps/web` renders it; a second declaration on either side is a parallel copy
// free to drift, which is what `check_source_of_truth.py` exists to refuse.
export interface AccessPerson {
  contactId: number;
  name: string;
  phone: string | null;
  email: string | null;
  state: AccessState;
  /** The live token, or null — a dead link never travels. */
  token: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  usedAt: string | null;
  /** Who bound it (AC11). */
  usedByEmail: string | null;
  revokedAt: string | null;
  /** AC12 — something that HAPPENED, never a seventh state. */
  refusedReason: string | null;
  /**
   * Their newest sign-in (`activity_events` kind `login`, by the address that
   * bound the link), or null — who signed in most recently is who a message
   * about nothing sent goes to (messages-one-shape AC7a).
   */
  lastSignInAt: string | null;
  /** Every address this person's links were ever bound with — who sent a proof, across resets (PR #267 review). */
  signInEmails: string[];
}

// --- the customer's own read (GET /api/portal/me) ------------------------------
// household-app-access built it as Home's one read (AC9a); customer-sign-in-
// registration widens it with everything the registration steps and the
// receipt draw. STILL ONE READ: the wizard, the receipt and Home all take the
// same shape, so a field cannot mean two things on two screens.

/** Which provider vouched for the sign-in address — the mark beside it (AC9). */
export type SignInProvider = "google" | "microsoft" | "apple";

/** The signed-in person, as the contact the desk typed — not as the provider says. */
export interface PortalContact {
  id: number;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  /** The sign-in address once the details step has written it; the desk's before. */
  email: string | null;
  /** ISO timestamp, or null while they are still *Signing up* (AC1, AC2). */
  registeredAt: string | null;
  /** Null when the session predates the field — the mark is then omitted. */
  provider: SignInProvider | null;
}

/** Everyone on the household, for the pick rows and the receipt's People group (AC10, AC31). */
export interface PortalPerson {
  id: number;
  name: string;
  phone: string | null;
  registeredAt: string | null;
}

/**
 * Where a dog's card stands, derived from its newest `card` proof.
 * `none` — no card ever sent; `added` — a card is on file with the dates the
 * customer claimed. The desk's verdict on it (Accept, Not accepted) is row 9's
 * and is not read here; the receipt speaks in the card's own words (AC32).
 */
export type CardState = "none" | "added";

export interface PortalCard {
  state: CardState;
  /** The claimed annual date, in the `"MAR 27"` shape `vaccines_due` uses (AC42). */
  validUntil: string | null;
  /** The claimed kennel-cough date, same shape. */
  kcUntil: string | null;
  /**
   * Patricia's answer on the newest card (vaccination-cards), or null when no
   * card is on file. `paused` is three in a row not accepted (AC9); `at` is
   * when the state began — the upload, or the verdict — so Home can keep an
   * accept on screen for seven days (AC3); `byDesk` marks a card Patricia
   * added herself (AC8).
   */
  review?: { state: "checking" | "accepted" | "not_accepted" | "paused"; at: string; byDesk: boolean } | null;
  /** The newest card's own id and type, when there is one — the drawer draws its thumbnail and opens it (Freddi, 2026-10-13). */
  proofId?: string;
  mime?: string;
}

/** A dog as the registration step and the receipt draw it (AC17–AC25, AC32). */
export interface PortalDog extends OwnerDog {
  /** The desk's own annual date — shown, never written here (AC26). */
  vaccinesDue?: string | null;
  /** The desk's own kennel-cough date. */
  kcDue: string | null;
  /** Medication instructions, or null — the dogs step writes it (AC28). */
  meds: string | null;
  card: PortalCard;
}

/**
 * A request Patricia declined, for the seven days Home says so (accept-or-
 * decline AC11, AC12). The stay itself is hidden — nothing was held and
 * nothing is owed — and the notice carries only the dogs and the dates, in
 * neutral words: she gives no reason and the app invents none (231).
 */
export interface PortalDeclined {
  id: string;
  checkIn: string;
  checkOut: string;
  /** The dogs' names, one each — by id on the server, so a comma in a name is not a second dog. */
  dogs: string[];
  /** When she declined it, ISO — the notice expires seven days on. */
  decidedAt: string;
}

/**
 * The fact that decides a request (accept-or-decline AC3, AC13): per capacity
 * line over the request's nights, the busiest night's occupied count against
 * the limit in force, the request's own dogs on that line, and the level the
 * customer's calendar would show. `lead` is the line the row prints —
 * *Big · 12 of 14 those nights*. `othersAccepted` is other requests already
 * accepted for those nights whose deposit is not in: they hold no place (§3.1)
 * and are said beneath, not counted.
 */
export interface BookingCapacity {
  nights: number;
  lines: Record<CapacityLine, { occupied: number; mine: number; soft: number | null; hard: number; level: DayLevel }>;
  lead: { line: CapacityLine; occupied: number; hard: number; level: DayLevel };
  othersAccepted: number;
}

export interface PortalMe {
  contactName: string;
  contact: PortalContact;
  contacts: PortalPerson[];
  address: string | null;
  /** The household's keep-apart flag — the dogs step's *With other dogs* starts from it. */
  doNotMix: boolean;
  dogs: PortalDog[];
  stays: PortalStay[];
  /** Requests declined in the last seven days (accept-or-decline AC11). */
  declined?: PortalDeclined[];
  /**
   * TODAY, AS THE KENNEL RECKONS IT — the Dublin business day, from the server.
   *
   * Every date judgement on the customer's screens — a vaccination expired or
   * current, a stay upcoming or past — was taken from `new Date()` in the
   * browser. A phone with a wrong clock, or one sitting the far side of a
   * timezone boundary, moved a MEDICAL date between valid and lapsed
   * (P2.32, 2026-09-15). One clock, and it is ours.
   */
  today: string; // ISO YYYY-MM-DD
}

/**
 * GET /api/portal/info — what the Info screen says (customer-home-stays AC9).
 * Every value is one of Patricia's settings; none is typed into the page. The
 * people on the household come from `/me`.
 */
export interface PortalInfo {
  handoverAm: string; // "09:00-10:00"
  handoverPm: string; // "17:00-18:00"
  /** Which halves of which weekdays are closed for handovers, e.g. ["sun:am"]. */
  closedWeekly: string[];
  eircode: string;
  bankAccountName: string;
  bankIban: string;
  whatsappNumber: string;
}

// The GET /api/owners?mode=page list envelope payload — mirrors the bookings
// list's { data, page } (owners-server-search-paginate schema.md). `total` is
// computed on the first page (offset 0) only; load-more pages return null and
// the client keeps the figure it already has.
export interface OwnersPage {
  data: Owner[];
  page: { limit: number; offset: number; total: number | null; returned: number };
}

// The GET /api/owners/stats aggregate — the server-computed form of the stat
// strip + chip counts DogsOwnersView builds today, over ALL owners (AC4), so
// the page and the API cannot drift (owners-server-search-paginate schema.md).
export interface OwnerStats {
  ownersTotal: number;
  dogsTotal: number; // all dogs (incl. never-booked 'unknown' size); big+little under-counts
  dogsBig: number;
  dogsLittle: number;
  boarding: number;
  regulars: number;
  newcomers: number;
}

// --- Staff / rota / leave / pay (GET /api/staff, /shifts, /holidays,
// --- /pay-marks, /me/shifts) --------------------------------------------------
// The staff-domain wire types. These are the camelCase shapes the API emits —
// not the raw DB rows (rate arrives as float8, dates/timestamps ::text, and
// archived_at never crosses the wire).

// A helper as returned by GET /api/staff (and the staff POST/PATCH echoes).
export interface Staff {
  id: string;
  name: string;
  role: string; // free-text job title, defaulted to "Carer" — NOT an auth Role
  // null is meaningful — the helper is on the minimum wage; the client resolves
  // the effective rate against GET /api/settings. Selected ::float8, so a
  // number (not a numeric-string like Booking.total).
  rate: number | null;
  holsUsed: number;
  holsTotal: number;
  // The login email linking this record to the staff self-view; null = no login.
  email: string | null;
  // The mobile the staff daily brief is sent to; null = no number on file yet
  // (the gate the messaging sequence waits on). A value is a normalised E.164
  // number — the API rejects local-only numbers on write.
  phone: string | null;
}

// One roster slot from GET /api/shifts (and the PUT assign echo). 8 slots/day:
// 0-3 AM, 4-7 PM; one staff per (date, slot).
export interface Shift {
  id: number;
  date: string; // ISO YYYY-MM-DD
  slot: number; // 0-7
  staffId: string | null; // null = the assignee was archived (slot reopened)
  kind: Period;
}

// One leave entry from GET /api/holidays (and the POST/PATCH echoes). `status`
// never crosses the wire — every entry is written 'approved' (no workflow).
export interface Holiday {
  id: number;
  staffId: string;
  fromDate: string; // ISO YYYY-MM-DD
  toDate: string; // ISO YYYY-MM-DD
  kind: LeaveKind;
  note: string | null;
}

// A helper's Fri–Thu week marked as paid — GET /api/pay-marks (timesheet AC4).
export interface PayMark {
  staffId: string;
  weekStart: string; // ISO YYYY-MM-DD (the week's Friday)
  paidAt: string; // ISO timestamp
}

// --- Staff self-view (GET /api/me/shifts + the admin preview
// --- GET /api/staff/:id/shifts) ----------------------------------------------
// Built by apps/api me/window.ts buildSelfView — THE single builder behind both
// endpoints, so the admin preview is byte-for-byte the staff member's own view.

// One of the caller's own shifts inside the self-view window.
export interface SelfViewShift {
  date: string; // ISO YYYY-MM-DD
  slot: number; // 0-7
  kind: string; // 'am' | 'pm' (kept string-wide to match the builder's input)
}

// One Mon–Sun display week of the self-view: its bounds (clamped to the visible
// window), whether it is the current week, its shifts, and hours total
// (shifts × SLOT_HOURS).
export interface SelfViewWeek {
  from: string; // ISO YYYY-MM-DD (the week's Monday, or the window's start when clamped)
  to: string; // ISO YYYY-MM-DD (the week's Sunday, or the window's end when clamped)
  current: boolean; // true for the week containing today
  shifts: SelfViewShift[];
  hours: number;
}

// The whole self-view payload: the person's name, the visible month window
// [from, to], the unclamped current week bounds, and the ordered display weeks.
export interface SelfView {
  name: string;
  from: string; // ISO YYYY-MM-DD — the visible window start (1st of the month)
  to: string; // ISO YYYY-MM-DD — the visible window end (month end, or +7 days in the final week)
  currentWeekFrom: string; // ISO YYYY-MM-DD — the current week's (unclamped) Monday
  currentWeekTo: string; // ISO YYYY-MM-DD — the current week's Sunday
  weeks: SelfViewWeek[]; // ordered Mon–Sun display weeks across the window
}

// --- Staff Today briefing (GET /api/me/today, staff-food-at-house) -------------
// The staff self-view's page_view label contract — ONE home for the vocabulary
// the client records, the API's feed filter matches, and the admin activity
// surfaces roll up and display. Adding a staff tab = adding one entry here.
export const STAFF_PAGE_VIEW_PREFIX = 'staff-';
export const STAFF_PAGE_LABELS = {
  today: `${STAFF_PAGE_VIEW_PREFIX}today`,
  tomorrow: `${STAFF_PAGE_VIEW_PREFIX}tomorrow`,
  // Quoted key ON PURPOSE: key === label suffix === route path (/me/day-after)
  // is the invariant every entry keeps, and the flat key doubles as a display
  // word in the admin activity rollup (capitalise(tab)).
  "day-after": `${STAFF_PAGE_VIEW_PREFIX}day-after`,
  shifts: `${STAFF_PAGE_VIEW_PREFIX}shifts`,
  info: `${STAFF_PAGE_VIEW_PREFIX}info`,
} as const;
export type StaffTab = keyof typeof STAFF_PAGE_LABELS;
// Human display name per tab, for the admin activity feed ("My Shifts · Day
// after"). Explicit rather than derived from the key: the key is a wire/route
// token ('day-after'), and capitalising it renders "Day-after". Adding a staff
// tab = one entry here alongside the label above.
export const STAFF_TAB_DISPLAY: Record<StaffTab, string> = {
  today: "Today",
  tomorrow: "Tomorrow",
  "day-after": "Day after",
  shifts: "Shifts",
  info: "Info",
};
// The pre-tab recorder logged the whole self-view under this one label —
// historic rows carry it; no current client emits it.
export const STAFF_SELF_VIEW_LABEL = `${STAFF_PAGE_VIEW_PREFIX}self-view`;

// --- The admin app's page_view label contract --------------------------------
// The nav keys the admin client records as page views — the same keys routeMap's
// NAV declares. ONE home, so the feed's filter and the activity surfaces agree
// with what the client actually emits. Adding a nav section = one entry here
// alongside the routeMap item.
export const ADMIN_PAGE_LABELS = [
  "today",
  "bookings",
  "dogs",
  "schedule",
  "finances",
  "messages",
  "account",
] as const;

// Every page_view label the ACTIVITY FEED renders: the admin sections above plus
// the staff self-view pages (and the historic whole-page label).
//
// Admin section views used to be excluded from the feed — "the heatmap's job; a
// feed of every section open would be noise". Freddi asked on 2026-07-25 to see
// the pages people look at alongside the actions they perform, so they ride in
// now. This stays an EXACT allowlist matched by BOUND PARAMETER, never a prefix
// and never a LIKE: `label` on a page_view is client-supplied free text, so
// naming every acceptable value is what stops a fabricated label surfacing in
// the audit feed as though it were a real page.
export const FEED_PAGE_LABELS: readonly string[] = [
  ...ADMIN_PAGE_LABELS,
  ...Object.values(STAFF_PAGE_LABELS),
  STAFF_SELF_VIEW_LABEL,
];

// Self-scoped like /me/shifts: date + identity are decided server-side. The
// rostered gate is a data gate — foodDogs is ALWAYS empty when rostered=false.
// Since staff-daily-lookahead the gate day is TODAY for every briefing day:
// `rostered` means "the gate passed — this day is visible to you" (true on a
// look-ahead day the caller is personally off), and `shifts` stays the TARGET
// day's own kinds — empty on such a day.
export interface MeTodayFoodDog {
  id: string; // dog id — the briefing rows' stable identity
  name: string;
  breed: string | null; // shown muted beside the name — same-name dogs are the norm
  leavesOn: string; // ISO YYYY-MM-DD — the booking's checkout (exclusive)
}
// The staff briefing's "please wash" threshold: a stay of this many nights or
// more earns the wash-before-collection prompt. ONE source for the rule — the
// leaving query computes `wash` from it; change it here and both the data and
// the copy that describes it move together.
export const WASH_MIN_NIGHTS = 7;

// A dog leaving on the briefing day (checkout = date). It shows in the
// Leaving card ONLY — never in the food/meds/keep-apart lists (the dedup) —
// so its three flags ride along as tags. `wash` is the hygiene prompt for a
// stay of WASH_MIN_NIGHTS+ nights, computed server-side so the threshold lives
// in one place.
export interface MeTodayLeavingDog {
  id: string;
  name: string;
  breed: string | null;
  food: boolean; // food_at_house on the departing booking
  meds: boolean; // meds on the departing booking
  keepApart: boolean; // NOT mix_ok
  wash: boolean; // stay of WASH_MIN_NIGHTS nights or more — wash before collection
}
export interface MeToday {
  date: string; // ISO YYYY-MM-DD — the server-decided Dublin date
  rostered: boolean;
  shifts: ("am" | "pm")[]; // distinct kinds, am before pm
  // Dogs leaving today, shown ONLY here — their food/meds/keep-apart become
  // tags, so a leaving dog never also appears in the three lists below.
  leavingDogs: MeTodayLeavingDog[];
  // One shape per briefing card; all three read the same booking-flag model
  // (food_at_house / meds / NOT mix_ok) over the locked spansDay rule, and
  // all three EXCLUDE dogs leaving today (checkout > date) — those are in
  // leavingDogs.
  foodDogs: MeTodayFoodDog[];
  medsDogs: MeTodayFoodDog[];
  keepApartDogs: MeTodayFoodDog[];
}

// --- Session (GET /api/me) ----------------------------------------------------
// The SPA-boot session shape. NOTE: the one read NOT wrapped in { data } — the
// client reads the fields off the body directly.
export interface Me {
  email: string;
  name: string;
  picture: string | null; // provider avatar URL, when the IdP supplied one
  // The live allowlist role, resolved server-side per request. AllowlistRole,
  // not Role: since portal-foundations a KV entry may say `customer`, and this
  // field reports what actually resolved rather than what the desk expects. A
  // customer reaching a desk surface is refused by every gate — no requireRoles
  // call on the desk names the word — and visibleNav gives them no nav at all.
  role: AllowlistRole;
}

// --- Payments ----------------------------------------------------------------
// One received sum against one booking. Embedded in BookingDetail so the modal
// can render the ledger it is editing.
export interface Payment {
  id: string;
  amount: number;
  receivedOn: string; // 'YYYY-MM-DD' — when the money arrived
  source: PaymentSource;
  // Which of the modal's two figures this row belongs to. Deliberately NOT
  // derivable from `source` — see PAYMENT_KINDS in enums.ts.
  kind: PaymentKind;
  /** The screenshot this row was recorded from, or linked to (deposit-proof AC10, AC12a); null for desk-typed money. */
  proofId?: string | null;
  estimated: boolean; // source === 'import': the date is the stay's check-out, not a record
  // When this row was CLEARED, or null while it stands. A payment entered in
  // error is voided, never deleted and never negated (Freddi, 2026-08-15):
  //
  // - Deleting it leaves no trace, which is why removing one used to need a
  //   confirmation step. Keeping the row is what makes clearing it a one-press
  //   act — the record survives, so the mistake is recoverable.
  // - A reversing −€50 row would be a LIE. A negative amount in this ledger
  //   already means a refund, a credit, or arrears carried forward (17 imported
  //   rows are exactly that), and no money went back when somebody mistyped.
  //
  // A voided row holds no editable slot and feeds no figure; it is shown as
  // history. Every SUM over this table excludes it.
  voidedAt: string | null; // ISO timestamp
}

// --- Finances (GET /api/finances/overview) -----------------------------------
// The revenue-analytics contract, shared by the API aggregator and the web
// FinancesPage so the two cannot drift. One row per month across the covered
// range.
//
// ONE BASIS, deliberately (Freddi, 2026-07-30). Revenue is what the nights
// actually served in that month were worth — ACCRUAL, spread across the stay
// rather than landed whole in its check-in month. A stay checking in 28 July and
// leaving 10 August puts four nights in July and nine in August; recognising all
// thirteen in July is how the page came to report €13,710 of August's nights as
// July revenue.
//
// A second `received` basis (money by payment date) was built and removed before
// it shipped: two figures on one page asked the reader to know which question
// they were asking before they could read either. The payments ledger still
// RECORDS when money arrives — see the Payment type above — it simply does not
// report it here yet. See docs/decision/payments-ledger.md.
export interface FinanceMonth {
  ym: string; // 'YYYY-MM'
  year: number;
  month: number; // 0-11
  partial: boolean; // the current calendar month, still in progress
  forward: boolean; // a future month — committed bookings only (the forward book)
  booked: number; // value earned, pro-rata by nights served in this month
  wages: number; // staff cost (shifts × SLOT_HOURS × effective rate) — estimated
  cancelled: number; // value lost to cancellations, at check-in month (nothing was served)
  count: number; // non-cancelled stays STARTING this month (a stay is one event, not a spread)
  dogNights: number; // dog-nights served in this month
  area: { big: number; little: number; mix: number }; // splits of `booked`, so they sum to it
  newRev: number; // first-booking customers — a split of `booked`
  repeatRev: number; // returning customers — a split of `booked`
}

export interface FinanceMeta {
  weekByYear: Record<number, number>; // value earned in the trailing 7 days, per year
  wagesEstimated: boolean; // wages are modelled from shifts, not actual payroll
  source: string; // 'live' | a snapshot id | 'synthetic'
}

export interface FinanceData {
  series: FinanceMonth[];
  meta: FinanceMeta;
}

// GET /api/finances/daily — one row per calendar day. The monthly series cannot
// answer "what did we make on Saturday", and the figure was not derivable at all
// until revenue stopped being recognised whole in a stay's check-in month: a
// per-day number computed from that basis would have shown a stay's entire value
// on its arrival day and nothing for the nights it actually served.
export interface FinanceDay {
  date: string; // 'YYYY-MM-DD'
  earned: number; // each stay's nightly value, on every night it actually served
  // What produced that figure, so the chart's hover can explain the bar rather
  // than restate its height. Both count the stays IN RESIDENCE that night, on
  // the same join as `earned` — so dogs/stays and the money can never describe
  // different sets of bookings.
  stays: number; // stays in residence that night
  dogs: number; // dogs across them (a dogless stay counts as one — the app's rule)
}

// --- Admin activity view (GET /api/activity/*) -------------------------------
// The admins-only Account ▸ Activity surfaces, over Story 1's activity_events
// log. One window governs all three reads; 'week' is the bounded default.
export type ActivityWindow = "week" | "month" | "all";

// GET /api/activity/overview — per-user metrics, keyed by email. The web client
// left-joins this onto the allowlist for the People columns, so a never-signed-in
// user (absent here) still renders as never / 0 / —.
export interface ActivityOverviewRow {
  email: string;
  lastSeen: string | null; // ISO timestamp of the most recent event, any kind
  logins: number; // login events in the window
  lastSection: string | null; // label of the most recent page_view (a nav key)
}

// GET /api/activity/feed — one logged event (action or sign-in), newest first.
export interface ActivityFeedEvent {
  id: number;
  at: string; // ISO timestamp (created_at)
  actor: string; // actor_email (the client shows the CURRENT allowlist role)
  kind: string; // 'action' | 'login' | 'page_view'
  label: string | null; // action → '<entity>.<verb>'; page_view + login → page key
  target: string | null; // action → affected id, when known
  durationMs: number | null; // page_view → dwell; null otherwise
  // The record whose EDITOR opens for this action, when the affected record has
  // no editor of its own. A dog is edited through its household, and a shift or
  // a holiday through the helper it belongs to — so the feed resolves the parent
  // server-side (the client has no endpoint that maps a dog to its owner, or a
  // shift to its helper). Null when the action needs no parent, or when the
  // record has since been deleted and the join finds nothing — in which case
  // the row falls back to the section it lived on.
  targetParent?: string | null;
}

// GET /api/activity/heatmap — one cell: page views by a person in a section.
export interface ActivityHeatCell {
  actor: string; // actor_email
  section: string; // page_view label (a nav key)
  count: number;
}

// GET /api/activity/logins — sign-ins per day over the window, oldest first. A
// sparse series (days with zero sign-ins are omitted); the client zero-fills the
// continuous date axis for the logins line chart.
export interface ActivityLoginPoint {
  day: string; // 'YYYY-MM-DD' (Europe/Dublin local day)
  count: number; // sign-ins on that day
}

// --- viewings ---------------------------------------------------------

export type NewViewing = typeof viewings.$inferInsert;

// A dog as DESCRIBED on the telephone. Both fields nullable, and that is the
// contract rather than laxity: callers often give a breed and no name, or a
// name and no breed, and log-a-viewing AC3 requires both rows to survive.
//
// Note the inverse landmine in OwnerForm.save — `if (!d.name.trim()) continue`
// silently discards a row with a breed and no name. Nothing on this path may
// copy that, and book-them-in has to reckon with it on the way across.
export interface ViewingDog {
  name: string | null;
  breed: string | null;
}

// GET/POST/PATCH /api/viewings — the wire shape. `created_at` and `updated_at`
// are deliberately absent: no surface reads them, and an unread field on a wire
// type is a field somebody will later read by accident.
export interface Viewing {
  id: string;
  contactName: string;
  phone: string | null;
  viewingOn: string; // ISO YYYY-MM-DD
  slot: ViewingSlot;
  status: ViewingStatus;
  dogs: ViewingDog[];
  notes: string | null;
  // Present ⇒ this viewing produced a customer. NOT a status — see ADR-3. The
  // desk's `Book` action keys its visibility on THIS, never on `status`: a
  // viewing can be Visited with a household on file and no booking, and that is
  // precisely the state that most needs the action.
  bookingId: string | null;
  // Present ⇒ the household this viewing became. Written the moment the
  // household saves; read by `Book`, which opens a booking for it rather than
  // creating the household again (review, PR #174).
  ownerId: string | null;
}

// GET /api/bookings/mixed — one row of the merged bookings-and-viewings list
// (viewings-in-the-bookings-list, architecture schema.md). A DISCRIMINATED
// UNION, not a widened Booking: a `Booking` with nullable money and a `kind` is
// the shape ADR-1 refused for the table, and refusing it for the wire is the
// same decision one layer up — nothing typed on `Booking[]` can receive a
// viewing by accident, because a ListRow is not assignable to a Booking.
export type ListRow =
  | { kind: "booking"; booking: Booking }
  | { kind: "viewing"; viewing: Viewing };

// The mixed list's page block. TWO totals and no `total`: a single figure over a
// mixed stream is the summed number decision/counting-rules.md forbids, and a
// field named `total` on this envelope would be summed by habit. A side that was
// not asked for (Type: Viewing alone) reports `null`, never 0 — there are
// bookings; they are simply not on this list.
export interface MixedPage {
  limit: number;
  offset: number;
  returned: number;
  totals: { bookings: number | null; viewings: number | null } | null;
}


// --- the customer portal (portal-foundations) --------------------------

// GET /api/settings — the portal's key/value block, alongside minimumWage. Every
// key in PORTAL_SETTING_KEYS is present: the 0027 seed guarantees a row, and a
// read that fell back to an invented default is exactly what R-SET-1 was missing
// two numbers for until Freddi gave them.
export type PortalSettings = Record<PortalSettingKey, string>;

// One dated override of a standing per-size limit (AC14, AC15). `limit: 0`
// means CLOSED to that size for the period — a number the screen renders as a
// word, because "0" in a limit column reads as a broken value.
//
// The range is INCLUSIVE at both ends: "no more bigs 12–19 Dec" closes the 19th.
export interface CapacityBlock {
  id: string;
  // kennel | big | little. NOT a dog size — the kennel line is every dog
  // whatever its size, and is where mix/house/unknown dogs are counted (AC16).
  line: CapacityLine;
  startsOn: string; // ISO YYYY-MM-DD
  endsOn: string; // ISO YYYY-MM-DD, inclusive
  // RED. At it the customer cannot submit; 0 means closed to that line.
  hardLimit: number;
  // AMBER. Between soft and hard the customer sees "limited space available"
  // and may still book. NULL means no warning band on this block — a closed
  // week does not want one.
  softLimit: number | null;
  note: string | null;
  createdBy: string;
  createdAt: string; // ISO timestamp
}

// One of Patricia's restricted drop-off and collection days (handover-days):
// a date or a run of dates, afternoon only or closed, for drop-offs and
// collections alike. INCLUSIVE at both ends, like CapacityBlock. A row beats the
// weekly rule on every day it covers.
export interface HandoverDay {
  id: string;
  startsOn: string; // ISO YYYY-MM-DD
  endsOn: string; // ISO YYYY-MM-DD, inclusive
  rule: HandoverDayRule;
  note: string | null;
  createdBy: string;
  createdAt: string; // ISO timestamp
}

/** A line's two thresholds, as they stand on one day. */
export interface LimitPair {
  soft: number | null;
  hard: number;
}import type { AccessState } from "./portal-access";


// ----- request-a-stay: the wire ----------------------------------------------
//
// What GET /api/portal/availability and POST /api/portal/bookings answer. The
// shapes the rules in stay-request.ts produce, carried as they are: the server
// judges, the wizard draws (AC24). AC25 pins both against a response captured
// from the running stack, not against a mock.


export interface PortalRangeAnswer extends RangeVerdict {
  /** The price, when the range can be booked; null while it cannot. */
  quote: StayQuote | null;
}

export interface PortalAvailability {
  /** Today, as the kennel reckons it. */
  today: string;
  /** The two handover windows, as Patricia keeps them (`09:00-10:00`). */
  handoverAm: string;
  handoverPm: string;
  days: AvailabilityDay[];
  /** What a chosen range says back — present only when one was asked about. */
  range: PortalRangeAnswer | null;
}

/** One dog's answers for this stay (R-REQ-2). */
export interface PortalRequestDog {
  id: string;
  meds: "none" | "yes";
  instructions?: string | null;
  keepApart: boolean;
}

export interface PortalRequestBody {
  /** The client's id for this send, so a lost reply can be asked again (AC22). */
  requestId: string;
  checkIn: string;
  checkOut: string;
  dropOff: Period;
  collection: Period;
  dogs: PortalRequestDog[];
  /** *Anything else Patricia should know?* — optional. */
  note?: string | null;
}

export interface PortalRequestReply {
  id: string;
  reference: string;
  status: BookingStatus;
  checkIn: string;
  checkOut: string;
  dropOff: Period;
  collection: Period;
  nights: number;
  total: number;
  deposit: number;
  inFull: boolean;
  dogs: { id: string; name: string; breed: string | null }[];
}
